import { useNavigate, useParams } from "react-router-dom";
import {
  TextField,
  Button,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  FormControlLabel,
} from "@mui/material";
import { useState, useEffect } from "react";
import {
  useAddRoleMutation,
  useLazyGetRoleByIdQuery,
  useUpdateRoleByIdMutation,
} from "../../services/accessRole";
import { showError, showToast } from "../../constants/toasts";
import usePermission from "../../hooks/userAccess";

const AddRole = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { isAdd } = usePermission({
    label: 'Manage Sub-Admin',
    page_permission: 'isAdd',
  })

  const [roleName, setRoleName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [addRole, { isLoading: isSaving }] = useAddRoleMutation();
  const [getById] = useLazyGetRoleByIdQuery();
  const [updateRole, { isLoading: isUpdating }] = useUpdateRoleByIdMutation();

  const modules = [
    { id: 1, name: "Dashboard" },
    { id: 2, name: "Manage Users" },
    { id: 3, name: "Rewards & Offers Management" },
    { id: 4, name: "Transactions & Loyalty Logs" },
    { id: 5, name: "Promotion Management" },
    { id: 6, name: "Store Management" },
    { id: 7, name: "Feedback, Reports & Logs" },
    { id: 8, name: "System Settings" },
    { id: 9, name: "Notification Center" },
    { id: 10, name: "Activity Logs" },
    { id: 11, name: "Manage Sub-Admin" },
    { id: 12, name: "Manage CMS Pages" },
  ];

  const [permissions, setPermissions] = useState(
    modules.map((module) => ({
      id: module.id,
      name: module.name,
      view: false,
      addEdit: false,
      delete: false,
    }))
  );

  // Fetch role data when editing
  const getDataByIdList = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await getById({ id }).unwrap();
      if (response.statusCode === 200) {
        setRoleName(response.data?.name || "");
        const permissionArray = Array.isArray(response.data?.permission)
          ? response.data.permission
          : [];
        const permissionBySidebarId: Record<
          number,
          { isView: boolean; isAdd: boolean; isEdit: boolean; isDelete: boolean }
        > = {};
        permissionArray.forEach((perm: any) => {
          const key = Number(perm?.sideBarId);
          if (!Number.isNaN(key)) {
            permissionBySidebarId[key] = {
              isView: !!perm?.isView,
              isAdd: !!perm?.isAdd,
              isEdit: !!perm?.isEdit,
              isDelete: !!perm?.isDelete,
            };
          }
        });
        setPermissions(
          modules.map((module) => {
            const p = permissionBySidebarId[module.id];
            return {
              id: module.id,
              name: module.name,
              view: p ? p.isView : false,
              addEdit: p ? !!(p.isAdd || p.isEdit) : false,
              delete: p ? p.isDelete : false,
            };
          })
        );
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showError(error?.message || error?.data?.message || "Failed to fetch role data");
    }
  };

  useEffect(() => {
    getDataByIdList();
  }, [id]);

  const handlePermissionChange = (id: number, field: string) => {
    setPermissions((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;

        const next: any = { ...row };
        const currentVal = Boolean((row as any)[field]);
        next[field] = !currentVal;

        // If enabling Add/Edit or Delete, ensure View is enabled
        if ((field === "addEdit" || field === "delete") && !currentVal) {
          next.view = true;
        }

        // Prevent turning off View while Add/Edit or Delete is enabled
        if (field === "view" && next.view === false && (next.addEdit || next.delete)) {
          next.view = true;
        }

        return next;
      })
    );
  };

  const handleSave = async () => {
    if (!roleName.trim()) {
      showError("Role name is required");
      return;
    }

    const permission = permissions.map((row) => ({
      sideBarId: row.id,
      label: row.name,
      isView: row.view,
      isAdd: row.addEdit,
      isEdit: row.addEdit,
      isDelete: row.delete,
    }));

    const body = {
      name: roleName,
      permission,
    };

    try {
      if (id) {
        const res = await updateRole({ id, body }).unwrap();
        if (res?.statusCode === 200) {
          showToast("Role updated");
          navigate(-1);
        }
      } else {
        const res = await addRole({ body } as any).unwrap();
        if (res?.statusCode === 200) {
          showToast("Role created");
          navigate(-1);
        }
      }
    } catch (error: any) {
      showError(error?.message || error?.data?.message || "Failed to save role");
    }
  };

  if (loading && id) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <div className="main_title">
        <h1>{id ? "Edit Role" : "Add Role"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button>
          {" "} - <button onClick={() => navigate("/manage-subadmin")}>Manage Sub-Admin</button>
          {" "} - <button onClick={() => navigate("/manage-subadmin/roles")}>Manage Roles</button>
          {" "} - {id ? "Edit Role" : "Add Role"}
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <div className="control_group w_50">
              <label htmlFor="role">Role Name</label>
              <TextField
                id="role"
                placeholder="Enter Role Name"
                hiddenLabel
                fullWidth
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
              />
            </div>
          </div>

          <TableContainer className="table_container mt_20">
            <Table>
              <TableHead>
                <TableRow hover>
                  <TableCell>Module Name</TableCell>
                  <TableCell align="center">Add/Edit</TableCell>
                  <TableCell align="center">Delete</TableCell>
                  <TableCell align="center">View</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permissions.map((row) => (
                  <TableRow hover key={row.id}>
                    <TableCell>
                      {row.name.includes(" - ") ? (
                        <span style={{ paddingLeft: 20 }}>{row.name.split(" - ")[1]}</span>
                      ) : (
                        <strong>{row.name}</strong>
                      )}
                    </TableCell>
                    {["addEdit", "delete", "view"].map((action) => (
                      <TableCell align="center" key={action}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean((row as any)[action])}
                              onChange={() => handlePermissionChange(row.id, action)}
                            />
                          }
                          name="check"
                          label=""
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {isAdd && <div className="form_btn">
            <Button
              className="btn btn_primary"
              disabled={isSaving || isUpdating || !roleName.trim()}
              onClick={handleSave}
            >
              {id ? "Update" : "Save"}
            </Button>
          </div>}
        </div>
      </div>
    </>
  );
};

export default AddRole;